import React, {useEffect, useState} from "react";

import {PageHead} from "@montana/shell/head";
import {Routes} from "@montana/shell/routes";
import {isBrowser} from "@montana/helpers/browser.helper";
import {detectInputMethod} from "@montana/helpers/detect-input-method.helper";
import {requestIdleCallback} from "@montana/helpers/request-callback.helper";
import {Footer} from "@montana/shell/footer";
import {Header} from '@montana/shell/header';
import {SSRProvider} from '@react-aria/ssr';
import {RouterProvider} from 'react-aria-components';
import {addAccessibilityHelperEvent} from "./helpers/accessibility.helper";
import {Global} from '@montana/shell/global';
import {insertJavaScript} from "./services/script-injection";
import {staticSettings} from "./helpers/static-settings.helper";
import {useRouter} from "./containers/router";
import {TRACEDOCK_LOAD_DELAY_MS, TRACEDOCK_SNIPPET} from "./constants/tracedock";
import {LayoutProvider} from "@montana/context/layout.context";
import {FirstRenderContext} from "@montana/context/first-render.context";
import {CrawlerContext} from "@montana/context/crawler.context";

interface IAppProps {
    crawlerMode?: boolean;
}


export const App: React.FC<IAppProps> = React.memo(({ crawlerMode = false }: IAppProps): JSX.Element => {

    const [isFirstRender, setIsFirstRender] = useState(true);
    const { location, push, replace: routerReplace } = useRouter();

    const navigate = React.useCallback((path: string, options?: any): void => {
        if (options?.replace) {
            routerReplace(path, options?.state);
        } else {
            push(path, options?.state);
        }
    }, [push, routerReplace]);

    if (isBrowser) {
        detectInputMethod();
        addAccessibilityHelperEvent();

        const whenReady = (): void => setIsFirstRender(false);

        useEffect((): void => {
            requestIdleCallback(whenReady);
        }, []);

        useEffect(() => {
            const {isDebugging, b2c} = staticSettings;

            if (!isFirstRender && b2c && !isDebugging) {
                setTimeout(() => insertJavaScript(TRACEDOCK_SNIPPET), TRACEDOCK_LOAD_DELAY_MS);
            }
        }, [isFirstRender]);

        useEffect(() => {
            if (location.hash) {
                const anchor = location.hash.replace('#', '');
                const element = document.getElementById(anchor);
                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView();
                    }, 0);
                }
            }
        }, [location.hash]);
    }

    return (
        <SSRProvider>
            <RouterProvider navigate={navigate}>
                <FirstRenderContext.Provider value={isFirstRender}>
                    <CrawlerContext.Provider value={crawlerMode}>
                    <PageHead />
                    <LayoutProvider>
                        <div className={`app app--${!isFirstRender ? 'interactive' : 'static'}`}>
                            <Header />
                            <main className="app__content">
                                <Routes />
                            </main>
                            <Footer />
                        </div>
                        <Global />
                    </LayoutProvider>
                </CrawlerContext.Provider>
            </FirstRenderContext.Provider>
        </RouterProvider>
    </SSRProvider>
    );
});
